/**
 * Environment configuration
 * Provides type-safe access to environment variables and feature flags
 */

export const env = {
  // API Configuration
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  clientBookingTermsVersion: import.meta.env.VITE_CLIENT_BOOKING_TERMS_VERSION || 'v4',

  // Environment Detection
  isDevelopment: import.meta.env.MODE === 'development',
  isProduction: import.meta.env.MODE === 'production',
  isStaging: import.meta.env.MODE === 'staging',
  isTest: import.meta.env.MODE === 'test',
  mode: import.meta.env.MODE,

  // Feature Flags
  features: {
    // Enable MSW (Mock Service Worker) in development and test environments
    enableMSW:
      import.meta.env.VITE_ENABLE_MSW === 'true' ||
      // import.meta.env.MODE === 'development' ||
      import.meta.env.MODE === 'test',

    // Enable debug logging
    enableDebugLogging:
      import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true' ||
      import.meta.env.MODE === 'development',

    // Enable performance monitoring
    enablePerformanceMonitoring: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true',

    // Enable error tracking (e.g., Sentry)
    enableErrorTracking:
      import.meta.env.VITE_ENABLE_ERROR_TRACKING === 'true' &&
      (import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging'),

    // Enable analytics
    enableAnalytics:
      import.meta.env.VITE_ENABLE_ANALYTICS === 'true' && import.meta.env.MODE === 'production',

    // Enable service worker for PWA features
    enableServiceWorker:
      import.meta.env.VITE_ENABLE_SERVICE_WORKER === 'true' &&
      import.meta.env.MODE === 'production',
  },

  // Development Configuration
  dev: {
    // Hot reload for MSW handlers
    hotReloadMSW:
      import.meta.env.VITE_HOT_RELOAD_MSW === 'true' || import.meta.env.MODE === 'development',

    // Show component boundaries in React DevTools
    showComponentBoundaries: import.meta.env.VITE_SHOW_COMPONENT_BOUNDARIES === 'true',

    // Enable strict mode warnings
    enableStrictMode: import.meta.env.VITE_ENABLE_STRICT_MODE !== 'false',
  },

  // Production Configuration
  prod: {
    // API timeout in milliseconds
    apiTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10),

    // Cache duration for SWR in milliseconds
    cacheTimeout: parseInt(import.meta.env.VITE_CACHE_TIMEOUT || '300000', 10), // 5 minutes

    // Enable source maps in production
    enableSourceMaps: import.meta.env.VITE_ENABLE_SOURCE_MAPS === 'true',
  },

  // External Service Configuration
  services: {
    // Error tracking service (e.g., Sentry DSN)
    errorTrackingDSN: import.meta.env.VITE_ERROR_TRACKING_DSN,

    // Analytics service ID
    analyticsId: import.meta.env.VITE_ANALYTICS_ID,

    // Performance monitoring service
    performanceMonitoringId: import.meta.env.VITE_PERFORMANCE_MONITORING_ID,
  },
} as const;

// Type assertion for Vite env
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL: string;
    readonly VITE_CLIENT_BOOKING_TERMS_VERSION?: string;
    readonly VITE_ENABLE_MSW?: string;
    readonly VITE_ENABLE_DEBUG_LOGGING?: string;
    readonly VITE_ENABLE_PERFORMANCE_MONITORING?: string;
    readonly VITE_ENABLE_ERROR_TRACKING?: string;
    readonly VITE_ENABLE_ANALYTICS?: string;
    readonly VITE_ENABLE_SERVICE_WORKER?: string;
    readonly VITE_HOT_RELOAD_MSW?: string;
    readonly VITE_SHOW_COMPONENT_BOUNDARIES?: string;
    readonly VITE_ENABLE_STRICT_MODE?: string;
    readonly VITE_API_TIMEOUT?: string;
    readonly VITE_CACHE_TIMEOUT?: string;
    readonly VITE_ENABLE_SOURCE_MAPS?: string;
    readonly VITE_ERROR_TRACKING_DSN?: string;
    readonly VITE_ANALYTICS_ID?: string;
    readonly VITE_PERFORMANCE_MONITORING_ID?: string;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// Environment validation
if ((env.isProduction || env.isStaging) && !env.apiBaseUrl) {
  throw new Error('VITE_API_BASE_URL is required in production and staging');
}

// Log environment configuration in development
if (env.isDevelopment && env.features.enableDebugLogging) {
  console.log('🔧 Environment Configuration:', {
    mode: env.mode,
    apiBaseUrl: env.apiBaseUrl,
    features: env.features,
  });
}
